// The review screen edits a local copy of the edition. Items need a stable key before they have an id, and highlights
// point at items by that key until the save sends them by position.
import type { AdminEdition, AdminItem, EditionSaveBody, ExcludedChange } from '@/types/releaseNotes';

export const TITLE_MAX = 90;
export const BODY_MAX = 400;
export const MAX_HIGHLIGHTS = 3;

export interface DraftItem extends AdminItem {
  key: string;
}

export interface DraftHighlight {
  itemKey: string;
  imageS3Key: string | null;
  imageAlt: string;
}

export interface EditorDraft {
  items: DraftItem[];
  highlights: DraftHighlight[];
}

let counter = 0;
const newKey = () => `new-${Date.now().toString(36)}-${(counter += 1)}`;

export function draftFromEdition(edition: AdminEdition): EditorDraft {
  return {
    items: edition.items.map(item => ({ ...item, key: item._id })),
    highlights: edition.highlights.map(h => ({ itemKey: h.itemId, imageS3Key: h.imageS3Key, imageAlt: h.imageAlt }))
  };
}

export function blankItem(area: string): DraftItem {
  return {
    key: newKey(),
    audience: 'everyone',
    area,
    type: 'improved',
    title: '',
    body: '',
    featureFlags: [],
    guideSlug: null,
    sources: { prs: [], linear: [] },
    evidence: null
  };
}

/** A left-out change brought back as an empty note for a person to write up; the API moves its sources across. */
export function restoredItem(change: ExcludedChange, area: string): DraftItem {
  return { ...blankItem(area), restoredFrom: change._id, sources: change.sources };
}

export function saveBody(draft: EditorDraft): EditionSaveBody {
  const indexByKey = new Map(draft.items.map((item, i) => [item.key, i]));
  return {
    items: draft.items.map(({ key: _key, ...item }) => item),
    highlights: draft.highlights.flatMap(h => {
      const itemIndex = indexByKey.get(h.itemKey);
      return itemIndex === undefined ? [] : [{ itemIndex, imageS3Key: h.imageS3Key, imageAlt: h.imageAlt }];
    })
  };
}

/** Removing an item also removes its highlight, so a save never points at a missing item. */
export function removeItem(draft: EditorDraft, key: string): EditorDraft {
  return { items: draft.items.filter(item => item.key !== key), highlights: draft.highlights.filter(h => h.itemKey !== key) };
}

export function moveItem(draft: EditorDraft, key: string, by: -1 | 1): EditorDraft {
  const from = draft.items.findIndex(item => item.key === key);
  const to = from + by;
  if (from < 0 || to < 0 || to >= draft.items.length) return draft;
  const items = [...draft.items];
  [items[from], items[to]] = [items[to], items[from]];
  return { ...draft, items };
}

export function updateItem(draft: EditorDraft, key: string, patch: Partial<DraftItem>): EditorDraft {
  return { ...draft, items: draft.items.map(item => (item.key === key ? { ...item, ...patch } : item)) };
}

export const isDirty = (draft: EditorDraft, saved: EditorDraft) => JSON.stringify(saveBody(draft)) !== JSON.stringify(saveBody(saved));

/** Left-out changes not yet restored in this draft. */
export const remainingExcluded = (excluded: ExcludedChange[], draft: EditorDraft) => {
  const restored = new Set(draft.items.flatMap(item => (item.restoredFrom ? [item.restoredFrom] : [])));
  return excluded.filter(change => !restored.has(change._id));
};
